import { Box, CircularProgress, Tooltip, tooltipClasses, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import styled from "@emotion/styled";
import { Fragment, useMemo } from "react";
import { channelTypes } from "components/PimDashboard/DashboardComponents/Buying/BuyingSheet/data";
import { roundDecimalValueUptoTwo } from "utils/RoundDecimalValue";
import { grey, orange } from "@mui/material/colors";

const HtmlTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "white",
		color: "rgba(0, 0, 0, 0.87)",
		maxWidth: 400,
		fontSize: theme.typography.pxToRem(12),
		border: "1px solid #dadde9",
	},
}));

const RenderQtyRosData = ({ column, data }) => {
	const totalQty =
		isNaN(Number(data[`SOLD_QTY_${column}`]) ?? 1) / Number(data[`COUNT_OPT_${column}`])
			? 0
			: Math.round(roundDecimalValueUptoTwo(Number(data[`SOLD_QTY_${column}`]) ?? 1) / Number(data[`COUNT_OPT_${column}`]));
	const ros = isNaN(roundDecimalValueUptoTwo(Number(totalQty / 180))) ? 0 : roundDecimalValueUptoTwo(Number(totalQty / 180));

	return (
		<Box width="100%" height="100%" display="flex" alignItems="center" justifyContent="center">
			{/* <span>{data[column]}</span> */}
			<HtmlTooltip
				title={
					<Fragment>
						<Typography variant="body2" fontWeight={600}>
							<span style={{ color: grey[600] }}>Total Sold:</span> {data[`SOLD_QTY_${column}`]}
						</Typography>
						<Typography variant="body2" fontWeight={600}>
							<span style={{ color: grey[600] }}>Options Count:</span> {data[`COUNT_OPT_${column}`]}
						</Typography>
						<Typography variant="body2" fontWeight={600}>
							<span style={{ color: grey[600] }}>ROS: </span>
							{ros}
						</Typography>
					</Fragment>
				}>
				<Typography variant="body2" fontWeight={600} width="60%">
					{totalQty ? totalQty : 0}
				</Typography>
			</HtmlTooltip>
			<Tooltip title="ROS" arrow>
				<span style={{ width: "25%", color: orange[900] }}>{ros}</span>
			</Tooltip>
		</Box>
	);
};

const buildDummyRows = (selectedStyle) => {
	const base = selectedStyle?.final_mrp || selectedStyle?.suggested_mrp || selectedStyle?.mrp || 1499;
	const channels = Object.keys(channelTypes);
	return [0, 1, 2, 3].map((idx) => {
		const row = {
			id: `${selectedStyle?.style_id || selectedStyle?.id || "mrp"}-${idx}`,
			mrp: base + idx * 100,
			TOTAL: 180 + idx * 25,
		};

		channels.forEach((channel, channelIdx) => {
			const qty = (idx + 1) * (channelIdx + 2) * 45;
			row[channel] = `${qty}`;
			row[`SOLD_QTY_${channel}`] = qty * 2;
			row[`COUNT_OPT_${channel}`] = channelIdx + 1;
		});

		row[`SOLD_QTY_TOTAL`] = row.TOTAL * 3;
		row[`COUNT_OPT_TOTAL`] = 3;
		return row;
	});
};

const HistoricalDataTable = ({ data, loading = false }) => {
	const rows = useMemo(() => buildDummyRows(data), [data]);

	const mrpHistoricalTableColumns = useMemo(
		() => [
			{ field: "mrp", headerName: "MRP", flex: 0.8, minWidth: 90, align: "center", headerAlign: "center" },
			...Object.keys(channelTypes).map((row) => ({
				field: `${channelTypes[row]?.key}`,
				headerName: channelTypes[row]?.name,
				flex: 1,
				minWidth: 110,
				align: "center",
				headerAlign: "center",
				renderCell: ({ field, row: currentRow }) => <RenderQtyRosData column={field} data={currentRow} />,
			})),
			{
				field: "TOTAL",
				headerName: "TOTAL",
				flex: 1,
				minWidth: 110,
				align: "center",
				headerAlign: "center",
				renderCell: ({ field, row: currentRow }) => <RenderQtyRosData column={field} data={currentRow} />,
			},
		],
		[],
	);

	return (
		<Box width="100%">
			{loading ? (
				<Box width="100%" height={130} display="flex" justifyContent="center" alignItems="center">
					<CircularProgress />
				</Box>
			) : (
				<DataGrid
					rows={rows}
					columns={mrpHistoricalTableColumns}
					hideFooter
					sx={{
						height: 170,
						border: `1px solid ${grey[300]}`,
						"& .MuiDataGrid-row": {
							border: "none",
						},
						"& .MuiDataGrid-cell": {
							padding: "2px",
							borderRight: `1px solid ${grey[200]}`,
						},
						"& .MuiDataGrid-columnHeader": {
							cursor: "pointer",
							borderRight: `1px solid ${grey[300]}`,
						},
						"& .MuiDataGrid-columnHeaders": {
							background: grey[300],
							border: "none",
							borderRadius: 1,
						},
						"& .MuiDataGrid-row:hover": {
							backgroundColor: "inherit",
						},
					}}
					columnHeaderHeight={35}
					rowHeight={30}
					disableRowSelectionOnClick
				/>
			)}
		</Box>
	);
};

export default HistoricalDataTable;
